<template>
  <div class="text-center">
    <!-- 🔄 Global Loading Dialog -->
    <v-dialog v-model="isLoading" hide-overlay persistent width="280">
      <v-card color="primary" dark>
        <v-card-text class="d-flex align-center justify-center">
          <v-progress-circular indeterminate color="white" class="mr-3" />
          Please stand by...
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 💳 Main Dialog -->
    <v-dialog v-model="dialog" max-width="580" persistent>
      <template v-slot:activator="{ on, attrs }">
        <v-btn color="primary" dark v-bind="attrs" v-on="on">
          <v-icon left>mdi-credit-card-outline</v-icon>
          Update Payment Information
        </v-btn>
      </template>

      <v-card rounded="xl">
        <!-- Header -->
        <v-card-title class="d-flex align-center justify-space-between grey lighten-4">
          <span class="font-weight-bold text-h6">Manage Payment Methods</span>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-divider />

        <!-- Info -->
        <v-card-text class="pb-0 pt-5 text-body-2">
          Unless a default card is assigned to this location, charges will be made using the
          default organization card.
        </v-card-text>

        <!-- Add Card Button -->
        <v-card-text class="pt-3 pb-0">
          <v-btn color="primary" class="rounded-pill" @click="newCardDialog = true" elevation="1">
            <v-icon left>mdi-plus</v-icon>
            Add New Card
          </v-btn>
        </v-card-text>

        <!-- 💳 Card List -->
        <v-card-text>
          <transition-group name="fade">
            <v-card
              v-for="item in cards"
              :key="item.id"
              class="card-tile my-3 px-4 py-3 d-flex align-center justify-space-between"
              outlined
            >
              <div class="d-flex align-center">
                <v-avatar size="36" class="mr-3" color="grey lighten-4">
                  <v-icon color="primary">mdi-credit-card-outline</v-icon>
                </v-avatar>

                <div class="text-left">
                  <div class="font-weight-medium">
                    {{ item.brand }} •••• {{ item.last4 }}
                  </div>
                  <div class="text-caption grey--text">
                    Expires {{ item.exp_month }}/{{ item.exp_year }}
                  </div>
                </div>
              </div>

              <div class="d-flex align-center">
                <v-chip
                  v-if="item.id === defaultCardId"
                  color="primary"
                  text-color="white"
                  small
                  class="font-weight-bold mr-2"
                >
                  Default
                </v-chip>

                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense>
                    <v-list-item
                      v-if="item.id !== defaultCardId"
                      @click="setDefaultCard(item)"
                    >
                      <v-list-item-title>
                        <v-icon small left color="primary">mdi-check</v-icon>
                        Make Default
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="confirmDelete(item)">
                      <v-list-item-title>
                        <v-icon small left color="red">mdi-delete</v-icon>
                        Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card>
          </transition-group>

          <v-alert v-if="!cards.length && !isLoading" type="info" text border="left" class="mt-4">
            No cards on file. Add one to start automatic billing.
          </v-alert>
        </v-card-text>

        <v-divider />
        <v-card-actions class="d-flex justify-end">
          <v-btn text color="primary" @click="dialog = false">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 🆕 Add Card Dialog -->
    <v-dialog v-model="newCardDialog" max-width="520">
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center justify-space-between grey lighten-4">
          <span class="font-weight-bold">Add a New Card</span>
          <v-btn icon @click="newCardDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="mt-3">
          <div class="text-body-2 mb-4">
            Easily recharge next time by putting a credit card on file.
          </div>
          <v-alert v-if="stripeConfigError" type="error" outlined dense>{{ stripeConfigError }}</v-alert>
          <card
            v-if="stripePublicKey"
            class="stripe-card"
            :class="{ complete }"
            :stripe="stripePublicKey"
            @change="complete = $event.complete"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions class="justify-space-between">
          <v-btn text @click="newCardDialog = false">Cancel</v-btn>
          <v-btn color="primary" rounded :disabled="!complete" @click="addCard">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save Card
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Card, createToken } from 'vue-stripe-elements-plus'
import settingsService from '../../Settings/service.js'
import locationService from '../service.js'
import stripeConfigService from '@/api/stripeConfig'

export default {
  name: 'billing-update-payment',
  props: ['locationId', 'defaultCardId'],
  components: { Card },
  data() {
    return {
      dialog: false,
      newCardDialog: false,
      isLoading: false,
      cards: [],
      complete: false,
      stripePublicKey: '',
      stripeConfigError: '',
    }
  },
  methods: {
    async loadStripeConfig() {
      this.stripePublicKey = ''
      this.stripeConfigError = ''
      try { this.stripePublicKey = (await stripeConfigService.getOrganizationConfig()).publishableKey }
      catch (error) { this.stripeConfigError = error.message }
    },
    async loadCards() {
      this.$emit('update')
      this.$Progress.start()
      try {
        const response = await settingsService.Billing.getCards()
        this.cards = response.data.data
        if (this.cards.length === 1) this.$emit('updateDefault', this.cards[0])
      } catch (err) {
        console.error(err)
        this.$Progress.fail()
      } finally {
        this.$Progress.finish()
      }
    },

    async addCard() {
      try {
        if (!this.stripePublicKey) throw new Error(this.stripeConfigError || 'Stripe payment setup is unavailable for this billing account.')
        this.isLoading = true
        const { token } = await createToken()
        await settingsService.Billing.addCard({ source: token.id })
        this.newCardDialog = false
        await this.loadCards()
        this.$toasted.show('Card added successfully!', {
          type: 'success',
          duration: 3000,
        })
      } catch (err) {
        this.$toasted.error(err.message, {
          position: 'top-right',
          duration: 3500,
        })
      } finally {
        this.isLoading = false
      }
    },

    async setDefaultCard(card) {
      this.isLoading = true
      this.$Progress.start()
      try {
        await locationService.Billing.setDefaultCard(this.locationId, {
          source: card.id,
        })
        this.$emit('updateDefault', card)
        await this.loadCards()
        this.$toasted.show('Default card updated.', { type: 'success', duration: 2500 })
      } catch (err) {
        console.error(err)
        this.$Progress.fail()
      } finally {
        this.isLoading = false
        this.$Progress.finish()
      }
    },

    async confirmDelete(card) {
      // 🔒 Prevent deleting if it's the only card
      if (this.cards.length === 1) {
        this.$swal.fire({
          icon: 'info',
          title: 'Cannot delete this card',
          text:
            'This is the only card on file. To delete it, please add another card first, ' +
            'then you can remove this one.',
          confirmButtonText: 'Got it',
          confirmButtonColor: '#1976d2',
        })
        return
      }

      // 🗑️ Confirmation for deleting when multiple cards exist
      const result = await this.$swal.fire({
        icon: 'warning',
        title: 'Delete Card?',
        text: `Are you sure you want to delete this card ending in ${card.last4}?`,
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d32f2f',
      })

      if (result.isConfirmed) {
        this.deleteCard(card)
      }
    },

    async deleteCard(card) {
      this.$Progress.start()
      try {
        await settingsService.Billing.deleteCard(card.id)
        await this.loadCards()
        this.$toasted.show('Card deleted.', { type: 'success', duration: 2500 })
      } catch (err) {
        console.error(err)
        this.$Progress.fail()
      } finally {
        this.$Progress.finish()
      }
    },
  },
  watch: {
    newCardDialog(value) { if (value) this.loadStripeConfig() },
    dialog(val) {
      if (val) {
        this.isLoading = true
        this.loadCards().finally(() => (this.isLoading = false))
      }
    },
  },
}
</script>

<style scoped>
.stripe-card {
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 14px;
  background-color: #fafafa;
  transition: box-shadow 0.3s ease;
}
.stripe-card.complete {
  box-shadow: 0 0 10px rgba(66, 165, 245, 0.5);
}
.card-tile {
  transition: all 0.2s ease;
}
.card-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
